/**
 * Central Event Bus for Product Configurator
 * 
 * ALL EVENTS MUST BE REGISTERED HERE
 * This prevents magic strings and makes events discoverable.
 * 
 * Usage:
 *   // Emit event
 *   window.eventBus.emit('DATES_CHANGED', { startDate, endDate, days });
 * 
 *   // Listen to event
 *   window.eventBus.on('DATES_CHANGED', ({ startDate, endDate, days }) => {
 *     // Handle event
 *   });
 * 
 *   // Debug events
 *   window.eventBus.debugMode = true;
 */

class EventBus {
  /**
   * Event Registry - Single source of truth for all events
   * 
   * Each event must have:
   * - name: The actual event string (domain:action format)
   * - description: What this event means and when it fires
   * - payload: Schema describing the payload structure
   */
  static EVENTS = {
    // Date Management Events
    DATES_CHANGED: {
      name: 'rental:datesChanged',
      description: 'Fired when rental dates are updated in cart. All items (main product, modules, addons) should reselect variants based on new dates.',
      payload: { 
        startDate: 'string (YYYY-MM-DD)', 
        endDate: 'string (YYYY-MM-DD)', 
        days: 'number (inclusive count)' 
      }
    },
    
    // Transaction Type Events
    TRANSACTION_TYPE_CHANGED: {
      name: 'product:transactionTypeChanged',
      description: 'Fired when user switches between Rent and Purchase modes. Triggers variant reselection and UI updates.',
      payload: {
        transactionType: 'string (Rent|Purchase)',
        isRental: 'boolean',
        sectionId: 'string (section identifier)'
      }
    },
    
    // Variant Selection Events
    VARIANT_SELECTED: {
      name: 'product:variantSelected',
      description: 'Fired when a variant is selected for any item (main product, module, or addon). Used to update prices and UI.',
      payload: { 
        itemType: 'string (main|module|addon)', 
        handle: 'string (product handle)', 
        variantId: 'number (variant ID)', 
        variant: 'object (full variant data)' 
      }
    },
    
    VARIANT_CHANGED: {
      name: 'product:variantChanged',
      description: 'Fired when the main product variant changes. Used to update price boxes, summary, and related UI elements.',
      payload: {
        variant: 'object (full variant data)',
        sectionId: 'string (section identifier)'
      }
    },
    
    // Price Update Events
    PRICE_UPDATED: {
      name: 'product:priceUpdated',
      description: 'Fired when price is fetched/calculated for an item. Used to update price displays and totals.',
      payload: { 
        itemId: 'string (unique item identifier)', 
        price: 'number (price in dollars)', 
        formattedPrice: 'string (formatted with currency symbol)' 
      }
    },
    
    // Cart Operation Events
    CART_UPDATED: {
      name: 'cart:updated',
      description: 'Fired when cart contents or attributes change. All cart-aware components should refresh.',
      payload: { 
        cart: 'object (full Shopify cart object)' 
      }
    },
    
    CART_RENTAL_STATUS_CHANGED: {
      name: 'cart:rentalStatusChanged',
      description: 'Fired when cart transitions between having rental items and not having rental items. Used to show/hide rental-specific UI.',
      payload: {
        hasRentalItems: 'boolean (true if cart has at least one rental item)',
        cart: 'object (full Shopify cart object)'
      }
    },
    
    // Date Validation Events
    VALIDATION_FAILED: {
      name: 'validation:failed',
      description: 'Fired when date validation fails. DateValidationUI displays toast or modal based on context.',
      payload: {
        validation: 'object (validation result from DateValidationService)',
        context: 'string (picker|cart|checkout)',
        blocking: 'boolean (true for modal, false for toast)'
      }
    },
    
    OPEN_DATE_EDITOR: {
      name: 'dateEditor:open',
      description: 'Request to open the date picker/editor. Used by validation UI to allow user to fix dates.',
      payload: {
        suggestedFix: 'object|null (optional suggested dates from validation)'
      }
    }
  };
  
  constructor() {
    this.listeners = {}; // Store event listeners by event name
    this.debugMode = false; // Enable for troubleshooting
    this.eventLog = []; // Store event history when debug mode is enabled
  }
  
  /**
   * Subscribe to an event
   * 
   * @param {string} eventKey - Key from EventBus.EVENTS (e.g., 'DATES_CHANGED')
   * @param {function} handler - Callback function to handle the event
   * @returns {function} Unsubscribe function
   * 
   * @example
   * const unsubscribe = window.eventBus.on('DATES_CHANGED', ({ startDate, endDate }) => {
   *   console.log('Dates changed:', startDate, endDate);
   * });
   * 
   * // Later, to unsubscribe:
   * unsubscribe();
   */
  on(eventKey, handler) {
    const event = EventBus.EVENTS[eventKey];
    
    if (!event) {
      console.error(`❌ EventBus: Unknown event key "${eventKey}". Check EventBus.EVENTS registry.`);
      console.error('Available events:', Object.keys(EventBus.EVENTS));
      return () => {}; // Return no-op unsubscribe function
    }
    
    if (typeof handler !== 'function') {
      console.error(`❌ EventBus: Handler must be a function for event "${eventKey}"`);
      return () => {};
    }
    
    // Initialize listeners array for this event
    if (!this.listeners[event.name]) {
      this.listeners[event.name] = [];
    }
    
    // Add handler
    this.listeners[event.name].push(handler);
    
    if (this.debugMode) {
      console.log(`✅ EventBus: Subscribed to "${event.name}" (${this.listeners[event.name].length} listeners)`);
    }
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners[event.name].indexOf(handler);
      if (index > -1) {
        this.listeners[event.name].splice(index, 1);
        if (this.debugMode) {
          console.log(`🔌 EventBus: Unsubscribed from "${event.name}"`);
        }
      }
    };
  }
  
  /**
   * Emit an event
   * 
   * @param {string} eventKey - Key from EventBus.EVENTS (e.g., 'DATES_CHANGED')
   * @param {object} payload - Data to pass to event handlers
   * 
   * @example
   * window.eventBus.emit('DATES_CHANGED', {
   *   startDate: '2024-03-15',
   *   endDate: '2024-03-20',
   *   days: 6
   * });
   */
  emit(eventKey, payload = {}) {
    const event = EventBus.EVENTS[eventKey];
    
    if (!event) {
      console.error(`❌ EventBus: Unknown event key "${eventKey}". Check EventBus.EVENTS registry.`);
      console.error('Available events:', Object.keys(EventBus.EVENTS));
      return;
    }
    
    // Log event in debug mode
    if (this.debugMode) {
      const timestamp = new Date().toISOString();
      const logEntry = {
        timestamp,
        eventKey,
        eventName: event.name,
        payload,
        listenerCount: (this.listeners[event.name] || []).length
      };
      
      this.eventLog.push(logEntry);
      
      console.log(`📢 EventBus: ${event.name}`, {
        payload,
        listeners: logEntry.listenerCount,
        timestamp
      });
    }
    
    // Call all listeners
    const handlers = this.listeners[event.name] || [];
    
    if (handlers.length === 0 && this.debugMode) {
      console.warn(`⚠️ EventBus: No listeners for "${event.name}"`);
    }
    
    handlers.forEach((handler, index) => {
      try {
        handler(payload);
      } catch (error) {
        console.error(`❌ EventBus: Error in listener ${index + 1} for "${event.name}":`, error);
      }
    });
  }
  
  /**
   * Remove all listeners for a specific event
   * 
   * @param {string} eventKey - Key from EventBus.EVENTS
   */
  off(eventKey) {
    const event = EventBus.EVENTS[eventKey];
    
    if (!event) {
      console.error(`❌ EventBus: Unknown event key "${eventKey}"`);
      return;
    }
    
    delete this.listeners[event.name];
    
    if (this.debugMode) {
      console.log(`🔌 EventBus: Removed all listeners for "${event.name}"`);
    }
  }
  
  /**
   * Remove all listeners for all events
   */
  clear() {
    this.listeners = {};
    this.eventLog = [];
    
    if (this.debugMode) {
      console.log('🧹 EventBus: Cleared all listeners');
    }
  }
  
  /**
   * Get event log (only available when debugMode is enabled)
   * 
   * @returns {array} Array of log entries
   */
  getEventLog() {
    return [...this.eventLog];
  }
  
  /**
   * Print event statistics
   */
  printStats() {
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('📊 EventBus Statistics');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    
    console.log('\n📋 Registered Events:');
    Object.entries(EventBus.EVENTS).forEach(([key, event]) => {
      const listenerCount = (this.listeners[event.name] || []).length;
      console.log(`  ${key} (${event.name}): ${listenerCount} listeners`);
    });
    
    if (this.eventLog.length > 0) {
      console.log(`\n📝 Event Log: ${this.eventLog.length} events`);
      console.log('Recent events:');
      this.eventLog.slice(-5).forEach(entry => {
        console.log(`  ${entry.timestamp}: ${entry.eventName}`);
      });
    }
    
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');
  }
}

// Create global singleton instance
if (!window.eventBus) {
  window.eventBus = new EventBus();
  
  console.log('✅ EventBus initialized', {
    debugMode: 'window.eventBus.debugMode = true',
    stats: 'window.eventBus.printStats()'
  });
}

// Export for use in modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = EventBus;
}
